/* Copyright (C) 2023 Advenio Tecnosys Pvt. Ltd.

*        All Rights Reserved

*

 * NOTICE: 

 * All information contained herein is, and remains

* the property of Advenio Tecnosys and its suppliers,

* if any.  The intellectual and technical concepts contained

* herein are proprietary to Advenio Tecnosys

* and its suppliers and may be covered by Indian, U.S. and Foreign Patents,

* patents in process, and are protected by trade secret or copyright law.

* Appropriation, redistribution, dissemination of this information or reproduction

* of this material is strictly forbidden unless prior written permission is obtained

* from Advenio Tecnosys Pvt. Ltd.

*

* Author: < Mylavarapu Sarath Sai, Amal Sabu, Ujjwal Singh >

*/

import { client, timezone } from "./";
import {
  //  getPatients,
  //  getPatientDetailById,
  //  deletePatient,
  //  savePatient,
  //  updatePatient,
  updateViewPatient,
  saveViewPatients,
  deleteAllViewPatients,
  deleteViewPatientById,
  getAllViewPatients,
  addOrUpdateTotalPatientsCount,
  getTotalPatientsCount,
  deleteTotalPatientsCount,
  addOrUpdateAllViewPatientsMrnsList,
  getAllViewPatientsMrnListFromIdb,
} from "database/offlineDb/indexDb";
import {
  getAuthTokenFromSessionStorage,
  toDataURL,
  getImageAspectRatio,
  getImageAspectRatioImage,
} from "helpers/common";
import { serializeViewPatientPersistence } from "../realtime/viewPatientPersistenceQueue";

export { serializeViewPatientPersistence };

let viewPatientRequestGeneration = 0;

const beginViewPatientRequest = () => ++viewPatientRequestGeneration;
const isCurrentViewPatientRequest = (generation) =>
  generation === viewPatientRequestGeneration;
const persistCurrentViewPatientRequest = (generation, operation) =>
  serializeViewPatientPersistence(operation, () =>
    isCurrentViewPatientRequest(generation)
  );

const hydrateViewPatientEye = async (images) => {
  if (!Array.isArray(images)) return images;
  const hydrated = images.map(async (image) => {
    const url = await toDataURL(image?.url);
    const aspectRatio = await getImageAspectRatio(url);
    const [imgHeight, imgWidth] = await getImageAspectRatioImage(url);
    return {
      ...image,
      url,
      aspectRatio,
      imgHeight,
      imgWidth,
      data: { ...image?.data, uploaded: true },
    };
  });
  const settled = await Promise.allSettled(hydrated);
  return settled.map((result) => result.value);
};

const hydrateViewPatient = async (patient) => ({
  ...patient,
  ...(patient?.left
    ? { left: await hydrateViewPatientEye(patient.left) }
    : {}),
  ...(patient?.right
    ? { right: await hydrateViewPatientEye(patient.right) }
    : {}),
});

const patientMatchesOrganization = (patient, organizationId) => {
  if (!Number.isSafeInteger(organizationId) || organizationId < 1) return false;
  const images = [patient?.left, patient?.right]
    .filter(Array.isArray)
    .reduce((all, eye) => [...all, ...eye], []);
  const explicitOrganizations = [
    patient?.orgz_id,
    ...images.map((image) => image?.data?.orgz_id ?? image?.orgz_id),
  ].filter((value) => value !== undefined && value !== null && value !== "");
  if (explicitOrganizations.length === 0) return false;
  return explicitOrganizations.every(
    (value) => Number(value) === organizationId
  );
};

const requirePatientOrganization = (patients, organizationId) => {
  if (!Number.isSafeInteger(organizationId) || organizationId < 1) {
    throw new Error("View Patient response organization is unavailable");
  }
  const source = Array.isArray(patients) ? patients : [];
  if (
    !source.every((patient) =>
      patientMatchesOrganization(patient, organizationId)
    )
  ) {
    throw new Error("View Patient response organization mismatch");
  }
  return source;
};

const viewPatientVisibleTuples = (patient) => {
  const tuples = new Set();
  for (const laterality of ["left", "right"]) {
    const images = patient?.[laterality];
    if (images !== undefined && !Array.isArray(images)) return null;
    for (const image of images || []) {
      const source = image?.data || image;
      const imageLaterality =
        source?.laterality === laterality ||
        (laterality === "left" && source?.laterality === "OS") ||
        (laterality === "right" && source?.laterality === "OD")
          ? laterality
          : null;
      if (!source?.guid || !source?.Model || !imageLaterality) return null;
      tuples.add(`${source.guid}|${imageLaterality}|${source.Model}`);
    }
  }
  return tuples;
};

const viewPatientBackupTuples = (patient) => {
  if (!Object.prototype.hasOwnProperty.call(patient, "backUpReport")) {
    return new Set();
  }
  if (
    !patient?.backUpReport ||
    !Array.isArray(patient.backUpReport.images)
  ) {
    return null;
  }
  const tuples = new Set();
  for (const image of patient.backUpReport.images) {
    const source = image?.data || image;
    const laterality =
      source?.laterality === "left" || source?.laterality === "OS"
        ? "left"
        : source?.laterality === "right" || source?.laterality === "OD"
        ? "right"
        : null;
    if (!source?.guid || !source?.Model || !laterality) return null;
    tuples.add(`${source.guid}|${laterality}|${source.Model}`);
  }
  return tuples;
};

const viewPatientDurableIdentities = (patient) => {
  if (!Object.prototype.hasOwnProperty.call(patient, "retinalImagesData")) {
    return new Set();
  }
  const details = patient?.retinalImagesData?.retinalImagesDetails;
  if (!details || typeof details !== "object" || Array.isArray(details)) {
    return null;
  }
  const identities = new Set();
  for (const [laterality, field] of [
    ["left", "imageLeftEye"],
    ["right", "imageRightEye"],
  ]) {
    const images = details[field];
    if (images !== undefined && !Array.isArray(images)) return null;
    for (const image of images || []) {
      const guid = image?.fileData?.guid || image?.data?.guid || image?.guid;
      if (!guid) return null;
      identities.add(`${guid}|${laterality}`);
    }
  }
  return identities;
};

const preserveOmittedViewPatientEvidence = (
  patients,
  cachedPatients,
  organizationId,
  requestToken
) => {
  if (
    !Array.isArray(patients) ||
    !Array.isArray(cachedPatients) ||
    !requestToken ||
    requestToken !== getAuthTokenFromSessionStorage()
  ) {
    return patients;
  }
  return patients.map((patient) => {
    const matches = cachedPatients.filter(
      (cached) =>
        String(cached?.mrn) === String(patient?.mrn) &&
        patientMatchesOrganization(cached, organizationId)
    );
    if (matches.length !== 1) return patient;
    const cached = matches[0];
    const evidenceFields = ["backUpReport", "retinalImagesData"];
    if (
      evidenceFields.some((field) =>
        Object.prototype.hasOwnProperty.call(patient, field)
      ) ||
      !evidenceFields.every((field) =>
        Object.prototype.hasOwnProperty.call(cached, field)
      )
    ) {
      return patient;
    }
    const incomingTuples = viewPatientVisibleTuples(patient);
    const cachedVisibleTuples = viewPatientVisibleTuples(cached);
    const cachedBackupTuples = viewPatientBackupTuples(cached);
    const cachedDurableIdentities = viewPatientDurableIdentities(cached);
    if (
      !incomingTuples ||
      incomingTuples.size === 0 ||
      !cachedVisibleTuples ||
      !cachedBackupTuples ||
      !cachedDurableIdentities
    ) {
      return patient;
    }
    const cachedTuples = new Set([
      ...cachedVisibleTuples,
      ...cachedBackupTuples,
    ]);
    if (
      !Array.from(incomingTuples).every(
        (tuple) =>
          cachedTuples.has(tuple) &&
          cachedDurableIdentities.has(tuple.split("|").slice(0, 2).join("|"))
      ) ||
      !Array.from(cachedBackupTuples).every((tuple) =>
        cachedDurableIdentities.has(tuple.split("|").slice(0, 2).join("|"))
      )
    ) {
      return patient;
    }
    return {
      ...patient,
      backUpReport: cached.backUpReport,
      retinalImagesData: cached.retinalImagesData,
    };
  });
};

export const getAllViewPatientsFromIDB = () => async (dispatch) => {
  const viewPatientsList = await getAllViewPatients();
  const totalPatientsCount = await getTotalPatientsCount();

  return dispatch({
    type: "GET_VIEW_PATIENTS",
    payload: {
      data: {
        patients: viewPatientsList.data,
        totalPatientsCount: totalPatientsCount,
      },
    },
  });
};

export const republishViewPatientsRealtime =
  (scope = {}) => async (dispatch) => {
    const isCurrent =
      typeof scope.isCurrent === "function" ? scope.isCurrent : () => true;
    if (!isCurrent()) return { outcome: "ignored" };
    if (scope.patient) {
      dispatch({
        type: "PATCH_VIEW_PATIENT_REALTIME",
        payload: { patient: scope.patient, realtime: true },
      });
      return { outcome: "applied" };
    }
    const viewPatientsList = await getAllViewPatients();
    const totalPatientsCount = await getTotalPatientsCount();
    if (!isCurrent()) return { outcome: "ignored" };
    dispatch({
      type: "REPUBLISH_VIEW_PATIENTS_REALTIME",
      payload: {
        data: {
          patients: viewPatientsList.data,
          totalPatientsCount,
        },
      },
    });
    return { outcome: "applied" };
  };

export const setViewPatientRealtimeDescriptor = (descriptor) => ({
  type: "SET_VIEW_PATIENT_REALTIME_DESCRIPTOR",
  payload: descriptor,
});

export const retireViewPatientRequests = () => () => {
  beginViewPatientRequest();
  return { outcome: "ignored" };
};

export const getSearchedPatient =()=>(dispatch)=>{
  client.get("/getSearchedPatient")
}
export const getViewPatientDetailsDateWise =
  (
    startDate,
    endDate,
    orgid,
    token,
    setLoader,
    selectedOrgIdForReview = null,
    persistedRowsPerPage
  ) =>
  async (dispatch) => {
    const generation = beginViewPatientRequest();
    const requestAuthToken = getAuthTokenFromSessionStorage();
    const stableRequestToken =
      token === requestAuthToken ? requestAuthToken : null;
    try {
      const response = await client
      .get("/getpatientmonthwise", {
        headers: {
          Accept: "*/*",
          authorization: requestAuthToken,
          startdate: startDate,
          enddate: endDate,
          orgid,
          ...(Number(selectedOrgIdForReview)
            ? { orgid_for_review: Number(selectedOrgIdForReview) }
            : {}),
          skip: 0,
          limit: persistedRowsPerPage,
          timezone,
        },
      });
      const effectiveOrgId =
        Number(selectedOrgIdForReview) || Number(orgid) || null;
      const patients = await Promise.all(
        requirePatientOrganization(
          response.data?.patients,
          effectiveOrgId
        ).map(hydrateViewPatient)
      );
      return persistCurrentViewPatientRequest(generation, async () => {
        if (!isCurrentViewPatientRequest(generation)) {
          return { outcome: "ignored" };
        }
        const cachedPatients = await getAllViewPatients();
        if (!isCurrentViewPatientRequest(generation)) {
          return { outcome: "ignored" };
        }
        const preservedPatients = preserveOmittedViewPatientEvidence(
          patients,
          cachedPatients?.data,
          effectiveOrgId,
          stableRequestToken
        );
        const preparedResponse = {
          ...response,
          data: { ...response.data, patients: preservedPatients },
        };
        await deleteAllViewPatients();
        if (!isCurrentViewPatientRequest(generation)) {
          return { outcome: "ignored" };
        }
        await deleteTotalPatientsCount();
        if (!isCurrentViewPatientRequest(generation)) {
          return { outcome: "ignored" };
        }
        for (const patient of preservedPatients) {
          if (!isCurrentViewPatientRequest(generation)) {
            return { outcome: "ignored" };
          }
          const payload = await saveViewPatients(patient);
          if (!isCurrentViewPatientRequest(generation)) {
            return { outcome: "ignored" };
          }
          dispatch({
            type: "SAVE_VIEW_PATIENTS",
            payload,
          });
        }
        const allMrnsList = preparedResponse.data?.allMrnsList || [];
        await addOrUpdateAllViewPatientsMrnsList(allMrnsList);
        if (!isCurrentViewPatientRequest(generation)) {
          return { outcome: "ignored" };
        }
        await addOrUpdateTotalPatientsCount(allMrnsList.length);
        if (!isCurrentViewPatientRequest(generation)) {
          return { outcome: "ignored" };
        }
        setLoader(false);
        dispatch({
          type: "GET_VIEW_PATIENTS",
          payload: preparedResponse,
        });
        return { outcome: "applied", response: preparedResponse };
      });
    } catch (err) {
      if (!isCurrentViewPatientRequest(generation)) {
        return { outcome: "ignored" };
      }
      setLoader(false);
      if (
        err.response?.data?.msg === "No patients found for the given dates."
      ) {
        await deleteAllViewPatients();
        await deleteTotalPatientsCount();
        dispatch({
          type: "GET_VIEW_PATIENTS_REJECTED",
          payload: err.response?.data.viewPatientList,
        });
      } else {
        console.error(JSON.stringify(err.response?.data, null, 2));
      }
      return { outcome: "fallback", error: err };
    }
  };

export const getViewPatientDetailsDateWiseAndPageWise =
  (
    startDate,
    endDate,
    limit,
    skip,
    setLoader,
    isToGetAllPatentsDataToPrint,
    selectedOrgIdForReview = null,
    expectedOrganizationId = null
  ) =>
  async (dispatch, getState) => {
    const generation = beginViewPatientRequest();
    setLoader(true);
    try {
      const stateOrganizationId = Number(
        getState?.()?.userStore?.userProfile?.orgz_id
      );
      const effectiveOrganizationId =
        Number(expectedOrganizationId) ||
        Number(selectedOrgIdForReview) ||
        stateOrganizationId;
      let mrnsList = await getAllViewPatientsMrnListFromIdb();
      if (!isCurrentViewPatientRequest(generation)) {
        return { outcome: "ignored" };
      }
      if (!mrnsList?.length) {
        setLoader(false);
        return { outcome: "ignored" };
      }
      mrnsList = JSON.stringify(mrnsList.slice(skip, skip + limit));
      const response = await client.get("/getpatientmonthwise", {
        headers: {
          Accept: "*/*",
          authorization: getAuthTokenFromSessionStorage(),
          timezone,
          startdate: startDate,
          enddate: endDate,
          // orgid,
          ...(Number(selectedOrgIdForReview)
            ? { orgid_for_review: Number(selectedOrgIdForReview) }
            : {}),
          mrnsList,
        },
      });
      const patients = await Promise.all(
        requirePatientOrganization(
          response.data?.patients,
          effectiveOrganizationId
        ).map(hydrateViewPatient)
      );
      return persistCurrentViewPatientRequest(generation, async () => {
        for (const patient of patients) {
          if (!isCurrentViewPatientRequest(generation)) {
            return { outcome: "ignored" };
          }
          const payload = await saveViewPatients(patient);
          if (!isCurrentViewPatientRequest(generation)) {
            return { outcome: "ignored" };
          }
          dispatch({
            type: "SAVE_VIEW_PATIENTS",
            payload,
          });
        }
        const viewPatientsList = await getAllViewPatients();
        if (!isCurrentViewPatientRequest(generation)) {
          return { outcome: "ignored" };
        }
        const totalPatientsCount = await getTotalPatientsCount();
        if (!isCurrentViewPatientRequest(generation)) {
          return { outcome: "ignored" };
        }
        dispatch({
          type: "GET_VIEW_PATIENTS",
          payload: {
            data: {
              patients: viewPatientsList.data,
              totalPatientsCount: totalPatientsCount,
            },
          },
        });

        if (isToGetAllPatentsDataToPrint) {
          dispatch({
            type: "UPDATE_PATIENT_SELECTED",
            payload: viewPatientsList.data,
          });
        }
        setLoader(false);
        return { outcome: "applied", response };
      });
    } catch (err) {
      if (!isCurrentViewPatientRequest(generation)) {
        return { outcome: "ignored" };
      }
      setLoader(false);
      console.error(JSON.stringify(err.response?.data, null, 2));
      dispatch({ type: "GET_VIEW_PATIENTS_REJECTED", payload: [] });
      return { outcome: "fallback", error: err };
    }
  };
export const saveViewPatientsToDb = (data) => (dispatch) => {
  // client
  //  .get("/getpatientmonthwise", {
  //   headers: {
  //    Accept: "*/*",
  //    authorization: getAuthTokenFromSessionStorage(),
  //    startdate: startDate,
  //    enddate: endDate,
  //    orgz_id,
  //   },
  //  })
  //  .then((res) => {
  //   console.log(res);
  //   dispatch({
  //    type: "GET_VIEW_PATIENTS",
  //    payload: res,
  //   });
  //  });
  dispatch({
    type: "SAVE_VIEW_PATIENTS",
    payload: saveViewPatients(data),
  });
};

export const getAdvancedSearchDetails =
  (data, token, orgid, setLoader, selectedOrgIdForReview = null) => async (dispatch) => {
    const generation = beginViewPatientRequest();
    try {
      const response = await client.get("/patientSearch", {
        headers: {
          Accept: "*/*",
          authorization: getAuthTokenFromSessionStorage(),
          fname: data.firstNameVal,
          mname: data.middleNameVal,
          lname: data.lastNameVal,
          email: data.emailVal,
          mrn: data.mrnVal,
          phone: data.phNumVal,
          orgid,
          ...(Number(selectedOrgIdForReview)
            ? { orgid_for_review: Number(selectedOrgIdForReview) }
            : {}),
        },
      });
      const responsePatients = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.finalData)
        ? response.data.finalData
        : [];
      const effectiveOrgId =
        Number(selectedOrgIdForReview) || Number(orgid) || null;
      const patients = await Promise.all(
        requirePatientOrganization(responsePatients, effectiveOrgId).map(
          hydrateViewPatient
        )
      );
      return persistCurrentViewPatientRequest(generation, async () => {
        await deleteAllViewPatients();
        if (!isCurrentViewPatientRequest(generation)) {
          return { outcome: "ignored" };
        }
        await deleteTotalPatientsCount();
        if (!isCurrentViewPatientRequest(generation)) {
          return { outcome: "ignored" };
        }
        for (const patient of patients) {
          if (!isCurrentViewPatientRequest(generation)) {
            return { outcome: "ignored" };
          }
          const payload = await saveViewPatients(patient);
          if (!isCurrentViewPatientRequest(generation)) {
            return { outcome: "ignored" };
          }
          dispatch({
            type: "SAVE_VIEW_PATIENTS",
            payload,
          });
        }
        await addOrUpdateAllViewPatientsMrnsList(
          patients.map((patient) => patient.mrn)
        );
        if (!isCurrentViewPatientRequest(generation)) {
          return { outcome: "ignored" };
        }
        await addOrUpdateTotalPatientsCount(patients.length);
        if (!isCurrentViewPatientRequest(generation)) {
          return { outcome: "ignored" };
        }
        const preparedResponse = {
          ...response,
          data: {
            patients,
            totalPatientsCount: patients.length,
          },
        };
        setLoader(false);
        dispatch({
          type: "GET_VIEW_PATIENTS",
          payload: preparedResponse,
        });
        return { outcome: "applied", response: preparedResponse };
      });
    } catch (err) {
      if (!isCurrentViewPatientRequest(generation)) {
        return { outcome: "ignored" };
      }
      setLoader(false);
      console.error(JSON.stringify(err.response?.data, null, 2));
      return { outcome: "fallback", error: err };
    }
  };

export const deleteViewDetails = (mrn, token) => (dispatch) => {
  const mrnArray = [];
  mrnArray.push(mrn);
  const data = { mrnArray };
  client
    .post("/updatePatient", data, {
      headers: {
        Accept: "*/*",
        authorization: getAuthTokenFromSessionStorage(),
        // orgz_id,
        //  enddate: endDate,
        //  orgz_id,
      },
    })
    .then(async (res) => {
      const patientsAfterDelete = await deleteViewPatientById(mrnArray);

      dispatch({
        type: "DELETE_VIEW_PATIENTS",
        payload: { patientsAfterDelete, noOfPatientsToDelete: 1 },
      });
    })
    .catch((err) => {
      console.log("view patient delete error", err);
    });
};

export const deleteBulkViewDetails = (array, token) => (dispatch) => {
  client
    .post("/updatePatient", { mrnArray: array }, {
      headers: {
        Accept: "*/*",
        authorization: getAuthTokenFromSessionStorage(),
        // orgz_id,
        //  enddate: endDate,
        //  orgz_id,
      },
    })
    .then(async (res) => {
      const patientsAfterDelete = await deleteViewPatientById(array);

      dispatch({
        type: "DELETE_VIEW_PATIENTS",
        payload: { patientsAfterDelete, noOfPatientsToDelete: array.length },
      });

      // console.log("787878", res);
    })
    .catch((err) => {
      console.log("view patient bulk view details err", err);
    });
};

export function selectedViewPatientListData(value) {
  return (dispatch) => {
    dispatch({
      type: "SELECTED_VIEW_PATIENT_LIST_DATA",
      payload: value,
    });
  };
}

export const triggerDrAnnotation =
  (mrn, setSnackbarMessage, row, authToken) => async (dispatch) => {
    authToken = authToken || getAuthTokenFromSessionStorage();
    try {
      const res = await client.post(
        "/annotation-patient/dr",
        { mrn },
        {
          headers: {
            Accept: "*/*",
            authorization: authToken,
          },
        }
      );
      const modifiedPatientRow = {
        ...row,
        left: [
          {
            data: {
              ...row?.left?.[0]?.data,
              expAnnotationStatus: 1,
            },
            url: row?.left?.[0]?.url,
          },
        ],
        right: [
          {
            data: {
              ...row?.right?.[0]?.data,
              expAnnotationStatus: 1,
            },
            url: row?.right?.[0]?.url,
          },
        ],
      };
      await updateViewPatient(modifiedPatientRow);

      const viewPatientsList = await getAllViewPatients();
      const totalPatientsCount = await getTotalPatientsCount();
      dispatch({
        type: "GET_VIEW_PATIENTS",
        payload: {
          data: {
            patients: viewPatientsList.data,
            totalPatientsCount: totalPatientsCount,
          },
        },
      });

      setSnackbarMessage({
        message: res.data.msg,
        variant: "success",
      });
    } catch (error) {
      console.log("triggerDrAnnotation error", error.response);
      setSnackbarMessage({
        message:
          error?.response?.data?.msg ||
          "Error While Triggering The Expert Annotation",
        variant: "error",
      });
    }
  };

// This endpoint is patient specific also, but fires for each image annotation.
export const drConsentActionPatientView =
  (
    row,
    guid,
    drConsentDetails,
    viewPatientTableProps,
    setSnackbarMessage,
    setCount,
    rowAfterFirstImgDoneRef,
    resetAllAnnotationDataInSingleImgManipulator,
    count,
    mrnSpecificDrAnnotationDetails,
    checkLateralityArrLength
  ) =>
  (dispatch) => {
    const dr_status = drConsentDetails?.primary?.disease || "";

    const sendData = {
      user_id: viewPatientTableProps.userStore?.userProfile?.user_id,
      images: [
        {
          drConsentDetails,
          guid,
          Model:
            viewPatientTableProps.algorithms ||
            row.left?.[0]?.data.Model ||
            row.right?.[0]?.data.Model,

          mrn: row?.mrn,
        },
      ],
    };
    client
      .put("/annotation/dr", sendData, {
        headers: {
          Accept: "*/*",
          authorization: getAuthTokenFromSessionStorage(),
          "Access-Control-Allow-Origin": "*",
        },
      })
      .then(async (res) => {
        if (!res.status[0] === 2) {
          //  console.log(res);
          throw new Error("Error in modifying.");
        }
        const {
          primary: { disease: doc_dis, category: doc_dis_catg },
        } = drConsentDetails;
        delete drConsentDetails.primary;

        let modifiedPatientRow;
        rowAfterFirstImgDoneRef.current
          ? (modifiedPatientRow = rowAfterFirstImgDoneRef.current)
          : (modifiedPatientRow = row);

        if (row?.left?.[0]?.data?.guid === guid) {
          modifiedPatientRow = {
            ...modifiedPatientRow,
            left: [
              {
                data: {
                  ...row?.left?.[0]?.data,
                  dr_status,
                  ...drConsentDetails,
                  doc_dis,
                  doc_dis_catg,
                  doc_agree_with_ai: drConsentDetails.docAgreeWithAi,
                },
                url: row?.left?.[0]?.url,
              },
            ],
          };
        } else {
          modifiedPatientRow = {
            ...modifiedPatientRow,
            right: [
              {
                data: {
                  ...row?.right?.[0]?.data,
                  dr_status,
                  ...drConsentDetails,
                  doc_dis,
                  doc_dis_catg,
                  doc_agree_with_ai: drConsentDetails.docAgreeWithAi,
                },
                url: row?.right?.[0]?.url,
              },
            ],
          };
        }
        rowAfterFirstImgDoneRef.current = modifiedPatientRow;
        await updateViewPatient(modifiedPatientRow);

        const viewPatientsList = await getAllViewPatients();
        const totalPatientsCount = await getTotalPatientsCount();
        dispatch({
          type: "GET_VIEW_PATIENTS",
          payload: {
            data: {
              patients: viewPatientsList.data,
              totalPatientsCount: totalPatientsCount,
            },
          },
        });
        // await dispatch({
        //   type: "GET_VIEW_PATIENTS",
        //   payload: await getAllViewPatients(),
        // });

        let isSuccessResponse = res.data?.success;
        if (count === checkLateralityArrLength - 1) {
          client
            .put("annotation/mrn-specific-dr", mrnSpecificDrAnnotationDetails, {
              headers: {
                Accept: "*/*",
                authorization: getAuthTokenFromSessionStorage(),
                "Access-Control-Allow-Origin": "*",
              },
            })
            .then(async (res) => {
              await updateViewPatient({
                ...modifiedPatientRow,
                mrnSpecificDrAnnotationDetails: mrnSpecificDrAnnotationDetails,
              });
              const viewPatientsList = await getAllViewPatients();
              const totalPatientsCount = await getTotalPatientsCount();
              return dispatch({
                type: "GET_VIEW_PATIENTS",
                payload: {
                  data: {
                    patients: viewPatientsList.data,
                    totalPatientsCount: totalPatientsCount,
                  },
                },
              });
              // await dispatch({
              //   type: "GET_VIEW_PATIENTS",
              //   payload: await getAllViewPatients(),
              // });
              isSuccessResponse = res.data.status === 200;
              if (isSuccessResponse) {
                setCount((count) => count + 1);
                setSnackbarMessage({
                  message: `Modified successfully!`,
                  variant: "success",
                });
                // setSnackbarMessage("Modified successfully!");
                resetAllAnnotationDataInSingleImgManipulator();
              }
            })
            .catch((error) => {
              isSuccessResponse = error?.response?.data.status === 200;
              if (error?.response?.data.status === 409) {
                // setSnackbarMessage("Failed to Modify!");
                setSnackbarMessage({
                  message: `doctor advice/doctor recommendation/doctor referral/doctor restrictions/diagnostic tests was already added`,
                  variant: "error",
                });
              } else {
                setSnackbarMessage({
                  message: `Failed to Modify`,
                  variant: "error",
                });
              }
            });
        }

        if (!res.data?.success) {
          console.log("dr annotation failed");
          return;
        }
        if (isSuccessResponse && count !== checkLateralityArrLength - 1) {
          setCount((count) => count + 1);
          setSnackbarMessage({
            message: `Modified successfully!`,
            variant: "success",
          });
          resetAllAnnotationDataInSingleImgManipulator();
        }

        // const consentHtmlElement = document.getElementById("dr_status" + guid);
        // if (consentHtmlElement) {
        //   consentHtmlElement.innerText = truncateAndCapitalizeOrGetStatusNameForStringStatus(dr_status);
        // }

        // clearSingleImageManipulatorModalDataForVpTable();
      })
      .catch((err) => {
        //  resetAllAnnotationDataInSingleImgManipulator();
        console.log({ err });
        setSnackbarMessage({
          message: `Failed to Modify`,
          variant: "error",
        });
      });
  };

export const drConsentActionForPatientSpecific = async (
  prescriptionList,
  drAdvice,
  drRecommendation,
  drRestrictions
) => {
  try {
    const response = await client.put(
      "/annotation/dr",
      {
        prescriptionList,
        drAdvice,
        drRecommendation,
        restrictions: drRestrictions,
      },
      {
        headers: {
          Accept: "*/*",
          authorization: getAuthTokenFromSessionStorage(),
          "Access-Control-Allow-Origin": "*",
        },
      }
    );
  } catch (error) {
    console.log(error); //
  }
};
export const triggerReview =
  (guid, setSnackbarMessage, row, authToken) => async (dispatch) => {
    authToken = authToken || getAuthTokenFromSessionStorage();

    try {
      const res = await client.post("/review", guid, {
        headers: {
          Accept: "*/*",
          authorization: authToken,
        },
      });
      const modifiedPatientRow = {
        ...row,
        left: [
          {
            data: {
              ...row?.left?.[0]?.data,
              review: 1,
            },
            url: row?.left?.[0]?.url,
          },
        ],
        right: [
          {
            data: {
              ...row?.right?.[0]?.data,
              review: 1,
            },
            url: row?.right?.[0]?.url,
          },
        ],
      };
      await updateViewPatient(modifiedPatientRow);

      const viewPatientsList = await getAllViewPatients();
      const totalPatientsCount = await getTotalPatientsCount();
      dispatch({
        type: "GET_VIEW_PATIENTS",
        payload: {
          data: {
            patients: viewPatientsList.data,
            totalPatientsCount: totalPatientsCount,
          },
        },
      });
      setSnackbarMessage({
        message: res.data.msg,
        variant: "success",
      });
    } catch (error) {
      console.log("triggerReview error", error.response);
      setSnackbarMessage({
        message:
          error?.response?.data?.msg || "Error While Triggering The  Review",
        variant: "error",
      });
    }
  };

export const updateReview =
  (
    guidAndStatus,
    laterality,
    setLaterality,
    row,
    setSnackbarMessage,
    clearSingleImageManipulatorModalDataForVpTable,
    setCount,
    count,
    rowAfterFirstImgDoneRef,
    authToken
  ) =>
  async (dispatch) => {
    authToken = authToken || getAuthTokenFromSessionStorage();

    try {
      const res = await client.put("/review", guidAndStatus, {
        headers: {
          Accept: "*/*",
          authorization: authToken,
        },
      });
      let modifiedPatientRow;
      rowAfterFirstImgDoneRef.current
        ? (modifiedPatientRow = rowAfterFirstImgDoneRef.current)
        : (modifiedPatientRow = row);
      if (laterality === "left") {
        modifiedPatientRow = {
          ...modifiedPatientRow,
          left: [
            {
              data: {
                ...row?.left?.[0].data,
                review: 2,
                status: guidAndStatus?.newStatusForReviewGuid
                  ? guidAndStatus.newStatusForReviewGuid
                  : modifiedPatientRow.left?.[0]?.data?.status,
              },
              url: row?.left?.[0]?.url,
            },
          ],
        };
      }

      if (laterality === "right") {
        modifiedPatientRow = {
          ...modifiedPatientRow,
          right: [
            {
              data: {
                ...row?.right?.[0].data,
                review: 2,
                status: guidAndStatus?.newStatusForReviewGuid
                  ? guidAndStatus.newStatusForReviewGuid
                  : modifiedPatientRow.right?.[0]?.data?.status,
              },
              url: row?.right?.[0]?.url,
            },
          ],
        };
      }
      rowAfterFirstImgDoneRef.current = modifiedPatientRow;

      await updateViewPatient(modifiedPatientRow);
      setSnackbarMessage({
        message: res.data.msg,
        variant: "success",
      });

      setCount((count) => count + 1);

      const viewPatientsList = await getAllViewPatients();
      const totalPatientsCount = await getTotalPatientsCount();
      dispatch({
        type: "GET_VIEW_PATIENTS",
        payload: {
          data: {
            patients: viewPatientsList.data,
            totalPatientsCount: totalPatientsCount,
          },
        },
      });

      // await dispatch({
      //   type: "GET_VIEW_PATIENTS",
      //   payload: await getAllViewPatients(),
      // });

      dispatch({ type: "UPDATE_REVIEW", payload: res.data });
    } catch (error) {
      console.log(error.response);
      setSnackbarMessage({
        message: "Error in updating review",
        variant: "error",
      });
    }
  };

export const diagnosticTestsForStatus =
  (statuscode, authToken) => async (dispatch) => {
    authToken = authToken || getAuthTokenFromSessionStorage();
    try {
      const res = await client
        .get("/diagnostic-test", {
          headers: {
            Accept: "*/*",
            authorization: authToken,
            statuscode: statuscode,
          },
        })
        .then(async (res) => {
          await dispatch({
            type: "GET_DIAGNOSTICS_TESTS",
            payload: await res.data,
          });
        });
    } catch (error) {
      await dispatch({
        type: "GET_DIAGNOSTICS_TESTS",
        payload: [],
      });
      console.log(error);
    }
  };

export const showAnnotationPatientsOnlyAction = () => (dispatch) => {
  dispatch({ type: "SHOW_ANNOTATION_PATIENTS_ONLY" });
};

// export const addDiagnosticTests =
//   (tests, setSnackbarMessage, handleToggleDiagnosticTestModal, authToken) =>
//   async (dispatch) => {
//     authToken = authToken || getAuthTokenFromSessionStorage();
//     try {
//       const res = await client
//         .post("/diagnostic-tests", tests, {
//           headers: {
//             Accept: "*/*",
//             authorization: authToken,
//           },
//         })
//         .then(async (res) => {
//           await dispatch({
//             type: "ADD_DIAGNOSTICS_TESTS",
//             payload: await res.data,
//           });
//           setSnackbarMessage({
//             message: `Test's added successfully`,
//             variant: "success",
//           });
//           handleToggleDiagnosticTestModal();
//         });
//     } catch (error) {
//       setSnackbarMessage({
//         message: `Failed to add Test's`,
//         variant: "error",
//       });
//       console.log(error);
//     }
//   };

// Only used when singleImageManipulator is open
// export function savePrescription(value) {
//   return (dispatch) => {
//     dispatch({
//       type: "SAVE_PRESCRIPTION",
//       payload: value,
//     });
//   };
// }
// export function saveMrnSpecificDrAnnotationDetails(value) {
//   return (dispatch) => {
//     dispatch({
//       type: "SAVE_MRN_SPECIFIC_DR_ANNOTATION_DETAILS",
//       payload: value,
//     });
//   };
// }
